import { useMemo } from 'react';
import type { ExperienceTimeSlotItemResource } from '@anyroad/api';
import type { TimeSlot } from '@anyroad/time-slot-selector';
import {
  isSlotPastRescheduleCutoff,
  mapExperienceTimeSlotToTimeSlot,
} from '../helpers';

/**
 * Hook to filter and transform time slot data based on reschedule cutoff time.
 *
 * Filters out time slots that are past the reschedule cutoff window
 * and transforms them into the format expected by the TimeSlotSelector component.
 *
 * @param timeSlotsData - Raw time slots data from the API
 * @param minutesBeforeCutoff - Number of minutes before a time slot that rescheduling is no longer allowed
 * @returns Array of available time slots in the format expected by TimeSlotSelector
 *
 * @example
 * ```typescript
 * const availableTimes = useAvailableTimeSlots(timeSlotsData, 1440); // 24 hours
 * // Returns only slots that are at least 24 hours in the future
 * ```
 */
export const useAvailableTimeSlots = (
  timeSlotsData: { items?: ExperienceTimeSlotItemResource[] } | undefined,
  minutesBeforeCutoff: number
): TimeSlot[] => {
  return useMemo(() => {
    // No data available
    if (!timeSlotsData?.items) {
      return [];
    }

    // Filter out slots past the reschedule cutoff and transform to TimeSlot format
    return timeSlotsData.items
      .filter((slot: ExperienceTimeSlotItemResource) => {
        // Keep slots that are NOT past the cutoff
        return !isSlotPastRescheduleCutoff(
          slot.date,
          slot.time,
          minutesBeforeCutoff
        );
      })
      .map(mapExperienceTimeSlotToTimeSlot);
  }, [timeSlotsData, minutesBeforeCutoff]);
};
